import type { IndexedCard } from "@punkrecords/types";

const OUTBOUND_UTM_SOURCE = "punkrecords.info";
const OUTBOUND_UTM_MEDIUM = "referral";
const OUTBOUND_UTM_CAMPAIGN = "outbound";

/**
 * Appends standard UTM params so external sites can attribute traffic to punkrecords.info.
 */
export function appendOutboundCampaign(url: string, utmContent: string): string {
  const u = new URL(url);
  u.searchParams.set("utm_source", OUTBOUND_UTM_SOURCE);
  u.searchParams.set("utm_medium", OUTBOUND_UTM_MEDIUM);
  u.searchParams.set("utm_campaign", OUTBOUND_UTM_CAMPAIGN);
  u.searchParams.set("utm_content", utmContent);
  return u.toString();
}

function stripParallel(name: string): string {
  return name.replace(/\s*\(Parallel\)\s*/gi, "").trim();
}

export function slugifyCardName(name: string): string {
  const normalized = stripParallel(name || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    // Cardmarket expects dot-separated names without separators (e.g., Monkey.D.Luffy -> MonkeyDLuffy).
    .replace(/\./g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return normalized || "Card";
}

export function buildCardmarketVersionsUrl(card: IndexedCard): string {
  const nameSlug = slugifyCardName(card.name);
  const number = (card.number || "").trim().toUpperCase();
  const base = `https://www.cardmarket.com/en/OnePiece/Cards/${nameSlug}-${number}/Versions`;
  return appendOutboundCampaign(base, "card_detail_cardmarket");
}

/**
 * Format a set code into the edition format used by Liga One Piece.
 * OP03 → OP-03, ST01 → ST-01, EB01 → EB-01, P → P
 */
function formatEdition(setCode: string): string {
  const match = /^([A-Za-z]+)(\d+)$/.exec(setCode);
  if (!match) return setCode;
  return `${match[1].toUpperCase()}-${match[2]}`;
}

export function buildLigaOnePieceUrl(card: IndexedCard): string {
  const name = stripParallel((card.name || "").trim());
  const number = (card.number || "").trim().toUpperCase();
  const setCode = (card.setCode || "").trim().toUpperCase();
  const edition = formatEdition(setCode);
  const cardParam = `${name} (${number})`;
  const params = new URLSearchParams({
    card: cardParam,
    ed: edition,
    num: number,
    view: "cards/card",
  });
  const base = `https://www.ligaonepiece.com.br/?${params.toString()}`;
  return appendOutboundCampaign(base, "card_detail_liga");
}
